import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { SourceDocument } from "../../../api/content/sourcesApi";
import { isAbortError } from "../../../api/http/httpClient";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";
import type { DocumentVersionRef } from "../../../shared/references/contentReferences";
import type { SourceDocumentReader } from "./sourceDocumentCache";

export const SourceDocumentContext = createContext<SourceDocumentReader | null>(null);

function useSourceDocuments(): SourceDocumentReader {
  const documents = useContext(SourceDocumentContext);
  if (documents === null) {
    throw new Error("Reading source documents requires SourceDocumentProvider.");
  }
  return documents;
}

/**
 * One exact document version, through the session's document cache; `null` without a
 * reference. If its read fails, it reads again once another view of it succeeds (such as
 * after "Try again" there), so all views of a version agree.
 */
export function useSourceDocument(reference: DocumentVersionRef): LoadHandle<SourceDocument>;
export function useSourceDocument(reference: DocumentVersionRef | null): LoadHandle<SourceDocument | null>;
export function useSourceDocument(reference: DocumentVersionRef | null): LoadHandle<SourceDocument | null> {
  const documents = useSourceDocuments();
  const exact = useExactVersion(reference);
  const load = useCallback(
    (signal: AbortSignal) => (exact === null ? Promise.resolve(null) : documents.read(exact, signal)),
    [documents, exact],
  );
  const handle = useLoad(load);
  useReadAgainWhenRead(documents, exact, handle.state.status === "failed", handle.reload);
  return handle;
}

/**
 * The name of a document version (`documentDisplayName`), or `null` until it is read or
 * if it can't be: a name stands in for the ID, so a failure here shows no notice. Only the
 * name is kept, not the text.
 */
export function useDocumentName(reference: DocumentVersionRef | null): string | null {
  const documents = useSourceDocuments();
  const exact = useExactVersion(reference);
  const load = useCallback(async (signal: AbortSignal) => {
    if (exact === null) {
      return null;
    }
    try {
      return await documents.name(exact, signal);
    } catch (caught) {
      if (isAbortError(caught)) {
        throw caught;
      }
      return null;
    }
  }, [documents, exact]);
  const { state, reload } = useLoad(load);
  const name = state.status === "loaded" ? state.value : null;
  useReadAgainWhenRead(documents, exact, state.status === "loaded" && name === null, reload);
  return name;
}

/** The reference as a value that stays the same while its ID and version do. */
function useExactVersion(reference: DocumentVersionRef | null): DocumentVersionRef | null {
  const documentId = reference?.documentId ?? null;
  const version = reference?.version ?? null;
  return useMemo(() => (documentId === null || version === null ? null : { documentId, version }), [documentId, version]);
}

function useReadAgainWhenRead(documents: SourceDocumentReader, exact: DocumentVersionRef | null, waiting: boolean, readAgain: () => void): void {
  useEffect(() => {
    if (!waiting || exact === null) {
      return;
    }
    return documents.watch(exact, readAgain);
  }, [documents, exact, waiting, readAgain]);
}
